import { Box, PageWrapper, Typography } from '@oresundsbron/bridge-ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { useFlags } from '~/components/Flags';
import { SEOMetadata } from '../../components/SEOMetadata';
import { useBridgeStatus } from '../../hooks/useBridgeStatus';
import { usePage } from '../../hooks/usePage';
import { ContentfulGrid } from '../ContentfulGrid';
import { BridgeInfo } from './BridgeInfo';
import { PassageTimeInformation } from './PassageTimeInformation';
import { QueueTimeInformation } from './QueueTimeInformation';
import SubscribeToTrafficInformation from './subscribeToTrafficInformation';
import {
  bgStatusBorderColors,
  bgStatusColors,
  bgStatusTextColors,
  bgStatusTexts,
} from './utils';
import { WeatherInformation } from './WeatherInformation';

export const TrafficInformation: FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation(['bridgeStatus', 'common'], {
    nsMode: 'fallback',
  });
  const featureFlags = useFlags();
  const flagShowPassageTime = !!featureFlags?.get('passage-time');

  const { seoMetadata, page } = usePage({ id });
  const { data, isStatusLoading } = useBridgeStatus();

  const bgColor = bgStatusColors[data.status] || 'bg-neutral-200';
  const textColor = bgStatusTextColors[data.status] || '';
  const borderColor = bgStatusBorderColors[data.status] || 'bg-neutral-400';
  const text = useMemo(
    () => t(bgStatusTexts[data.status ?? 'bridgeStatus.unknown']),
    [data.status, t]
  );

  return (
    <>
      <SEOMetadata items={seoMetadata} />
      <Box as="main">
        <Typography as="h1" className="sr-only">
          {page?.title}
        </Typography>
        <PageWrapper
          as="section"
          className={cx(bgColor, borderColor, 'border-b-2')}
        >
          <Typography className="sr-only" as="h2">
            {t('bridgeStatus.title')}
          </Typography>
          <Box className="col-start-2 pb-4 pt-12 md:pb-6 md:pt-16">
            <Typography
              intent="display"
              as="h3"
              className={cx(
                'flex flex-col items-center gap-y-5 md:gap-y-6',
                textColor
              )}
            >
              {isStatusLoading ? (
                <>
                  <Box className="h-[48px] w-[48px] animate-pulse rounded-full bg-neutral-400 sm:h-[60px] sm:w-[60px] md:h-[72px] md:w-[72px]" />
                  <Box className="h-[32px] w-1/2 animate-pulse bg-neutral-400 sm:h-[60px] md:h-[72px]" />
                </>
              ) : null}
              {!isStatusLoading ? (
                <>
                  <svg
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                    className="text-5xl sm:text-6xl md:text-7xl"
                  >
                    <mask
                      id="mask0_6804_15622"
                      style={{ maskType: 'alpha' }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="24"
                      height="24"
                    >
                      <rect width="24" height="24" fill="currentColor" />
                    </mask>
                    <g mask="url(#mask0_6804_15622)">
                      <path
                        d="M12 17C12.2833 17 12.521 16.904 12.713 16.712C12.9043 16.5207 13 16.2833 13 16V11.975C13 11.6917 12.9043 11.4583 12.713 11.275C12.521 11.0917 12.2833 11 12 11C11.7167 11 11.4793 11.0957 11.288 11.287C11.096 11.479 11 11.7167 11 12V16.025C11 16.3083 11.096 16.5417 11.288 16.725C11.4793 16.9083 11.7167 17 12 17ZM12 9C12.2833 9 12.521 8.904 12.713 8.712C12.9043 8.52067 13 8.28333 13 8C13 7.71667 12.9043 7.479 12.713 7.287C12.521 7.09567 12.2833 7 12 7C11.7167 7 11.4793 7.09567 11.288 7.287C11.096 7.479 11 7.71667 11 8C11 8.28333 11.096 8.52067 11.288 8.712C11.4793 8.904 11.7167 9 12 9ZM12 22C10.6167 22 9.31667 21.7373 8.1 21.212C6.88333 20.6873 5.825 19.975 4.925 19.075C4.025 18.175 3.31267 17.1167 2.788 15.9C2.26267 14.6833 2 13.3833 2 12C2 10.6167 2.26267 9.31667 2.788 8.1C3.31267 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.31233 8.1 2.787C9.31667 2.26233 10.6167 2 12 2C13.3833 2 14.6833 2.26233 15.9 2.787C17.1167 3.31233 18.175 4.025 19.075 4.925C19.975 5.825 20.6873 6.88333 21.212 8.1C21.7373 9.31667 22 10.6167 22 12C22 13.3833 21.7373 14.6833 21.212 15.9C20.6873 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6873 15.9 21.212C14.6833 21.7373 13.3833 22 12 22ZM12 20C14.2167 20 16.1043 19.221 17.663 17.663C19.221 16.1043 20 14.2167 20 12C20 9.78333 19.221 7.89567 17.663 6.337C16.1043 4.779 14.2167 4 12 4C9.78333 4 7.896 4.779 6.338 6.337C4.77933 7.89567 4 9.78333 4 12C4 14.2167 4.77933 16.1043 6.338 17.663C7.896 19.221 9.78333 20 12 20Z"
                        fill="currentColor"
                      />
                    </g>
                  </svg>
                  <Box as="span" className="text-center">
                    {text}
                  </Box>
                </>
              ) : null}
            </Typography>
          </Box>
          <BridgeInfo />
        </PageWrapper>
        <PageWrapper
          as="div"
          className={cx(
            'bg-neutral-200',
            'md:pb-18 mb-8 pb-10 pt-8 sm:pb-14 sm:pt-10 md:pt-12'
          )}
        >
          <QueueTimeInformation />
          {flagShowPassageTime && <PassageTimeInformation />}
          <WeatherInformation />
        </PageWrapper>
        {featureFlags.get('traffic-information-subscription-enabled') ===
        true ? (
          <PageWrapper
            as="div"
            className={cx(
              'bg-neutral',
              'md:pb-18 mb-8 pb-10 pt-8 sm:pb-14 sm:pt-10 md:pt-12'
            )}
          >
            <div className="col-start-2">
              <SubscribeToTrafficInformation></SubscribeToTrafficInformation>
            </div>
          </PageWrapper>
        ) : null}
        {!!page?.content ? <ContentfulGrid rows={page.content} /> : null}
      </Box>
    </>
  );
};
